import { useTranslation } from 'react-i18next';
import { WeekFilter } from './ui/week-filter';
import { weekToRange } from '../lib/weekFilter';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import PaginationFooter from './PaginationFooter';
import { RefreshCw, CheckCircle, XCircle, MapPin } from 'lucide-react';
import { fmtDateTimeWeek } from '../lib/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = {
  unknown_username: 'No such username',
  wrong_password: 'Wrong password',
  not_approved: 'Account not approved',
};

/**
 * LoginAuditLog — admin-only sign-in history: which username was used,
 * when, from which IP and the resolved location. Failed attempts are
 * recorded too so brute-force probing is visible.
 */
const LoginAuditLog = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState({ skip: 0, limit: 50 });
  const [username, setUsername] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [weekFilter, setWeekFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page.skip, limit: page.limit });
      if (username.trim()) params.set('username', username.trim());
      if (outcome !== 'all') params.set('success', outcome === 'success' ? 'true' : 'false');
      if (weekFilter) {
        const r = weekToRange(weekFilter);
        params.set('start_date', r.from);
        params.set('end_date', r.to);
      }
      const { data: d } = await axios.get(`${API}/login-events?${params}`);
      setData(d);
    } catch {
      setData({ items: [], total: 0 });
    } finally { setLoading(false); }
  }, [page.skip, page.limit, username, outcome, weekFilter]);

  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return fmtDateTimeWeek(iso) || iso; } catch { return iso; }
  };

  return (
    <Card data-testid="login-audit-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">{t('auto.misc.loginLog')}</CardTitle>
            <CardDescription>{t('auto.misc.loginLogHint')}</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="login-audit-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('auto.misc.refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-60"
            placeholder={t('auto.misc.filterByUsername')}
            value={username}
            onChange={(e) => { setUsername(e.target.value); setPage(p => ({ ...p, skip: 0 })); }}
            data-testid="login-audit-username-filter"
          />
          <WeekFilter value={weekFilter} onChange={(w) => { setWeekFilter(w); setPage(p => ({ ...p, skip: 0 })); }} testId="login-audit-week-filter" />
          <Select value={outcome} onValueChange={(v) => { setOutcome(v); setPage(p => ({ ...p, skip: 0 })); }}>
            <SelectTrigger className="w-44" data-testid="login-audit-outcome-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auto.misc.allAttempts')}</SelectItem>
              <SelectItem value="success">{t('auto.misc.successfulOnly')}</SelectItem>
              <SelectItem value="failed">{t('auto.misc.failedOnly')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Table data-testid="login-audit-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('auto.misc.when')}</TableHead>
              <TableHead>{t('auto.misc.username')}</TableHead>
              <TableHead>{t('auto.misc.account')}</TableHead>
              <TableHead>{t('auto.misc.result')}</TableHead>
              <TableHead>{t('auto.misc.ip')}</TableHead>
              <TableHead>{t('auto.misc.location')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">{t('auto.misc.loading')}</TableCell></TableRow>
            )}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6" data-testid="login-audit-empty">
                {t('auto.misc.noLoginAttemptsRecordedYet')}
              </TableCell></TableRow>
            )}
            {!loading && data.items.map((e) => (
              <TableRow key={e.id} data-testid={`login-event-${e.id}`}>
                <TableCell className="whitespace-nowrap font-mono text-xs">{fmt(e.created_at)}</TableCell>
                <TableCell className="font-medium">{e.username || '—'}</TableCell>
                <TableCell className="text-xs text-slate-600">
                  {e.full_name || '—'}
                  {e.role ? <span className="text-slate-400"> · {e.role}</span> : null}
                </TableCell>
                <TableCell>
                  {e.success ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" /> {t('auto.misc.success')}
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800" title={e.reason || ''}>
                      <XCircle className="h-3 w-3 mr-1" /> {REASONS[e.reason] || 'Failed'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{e.ip || '—'}</TableCell>
                <TableCell className="text-xs">
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-slate-400" />
                    {e.location || 'Unknown'}
                  </span>
                  {e.geo?.isp && <div className="text-slate-400">{e.geo.isp}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <PaginationFooter
          total={data.total}
          skip={page.skip}
          limit={page.limit}
          onChange={({ skip, limit }) => setPage({ skip, limit })}
          testIdPrefix="login-audit-pagination"
        />
      </CardContent>
    </Card>
  );
};

export default LoginAuditLog;
